export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'invoiced' | 'paid';

export interface UploadResponse {
  key: string;
}

export interface QuoteItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface StructuredData {
  client_name: string | null;
  items: QuoteItem[];
  grand_total: number;
  confidence_score: number;
  notes?: string;
  client_address?: string;
}

export interface ProcessQuoteResponse {
  id: string;
  user_id: string;
  audio_key: string;
  transcript: string;
  structured_data: StructuredData;
  created_at: string;
  status: QuoteStatus;
  client_id: string | null;
}

export interface QuoteListItem {
  id: string;
  client_name: string | null;
  grand_total: number;
  confidence_score: number;
  created_at: string;
  status: QuoteStatus;
}

export interface QuoteListResponse {
  quotes: QuoteListItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface UpdateQuoteInput {
  structured_data: {
    client_name: string | null;
    items: Array<{
      description: string;
      quantity: number;
      unit_price: number;
    }>;
    notes?: string;
    client_address?: string;
  };
  client_id?: string | null;
}

export interface UpdateStatusResponse {
  id: string;
  status: QuoteStatus;
  updated_at: string;
}

export interface JobPrepTool {
  name: string;
  quantity: number;
  note: string | null;
}

export interface JobPrepMaterial {
  name: string;
  quantity: string;
  note: string | null;
}

export interface JobPrepSafety {
  name: string;
  note: string | null;
}

export interface JobPrepResponse {
  tools: JobPrepTool[];
  materials: JobPrepMaterial[];
  safety: JobPrepSafety[];
  tips: string[];
  generated_at: string;
}

export interface QuoteScoreMissingItem {
  description: string;
  estimated_quantity: number;
  estimated_unit_price: number;
  reason: string;
}

export interface QuoteScorePricingNote {
  item_description: string;
  note: string;
  severity: 'info' | 'warning';
}

export interface QuoteScoreResponse {
  overall_score: number;
  missing_items: QuoteScoreMissingItem[];
  pricing_notes: QuoteScorePricingNote[];
  tips: string[];
  generated_at: string;
}

export interface EmailDraftRequest {
  language?: 'en' | 'et';
  contractor?: {
    companyName?: string;
    paymentTermDays?: number;
    validityDays?: number;
  };
}

export interface EmailDraftResponse {
  subject: string;
  body: string;
  generated_at: string;
}

export interface ClientListItem {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  quote_count: number;
}

export interface ClientListResponse {
  clients: ClientListItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface ClientQuote {
  id: string;
  grand_total: number;
  status: QuoteStatus;
  created_at: string;
}

export interface ClientDetail {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  reg_code: string | null;
  vat_number: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  quotes: ClientQuote[];
}

export interface CreateClientInput {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  reg_code?: string;
  vat_number?: string;
  notes?: string;
}

export interface UpdateClientInput {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  reg_code?: string;
  vat_number?: string;
  notes?: string;
}

export interface AccountDeleteResponse {
  deleted: {
    quotes: number;
    clients: number;
    audio_files: number;
    quote_requests: number;
    contractor_profiles: number;
  };
}

export interface AccountExportResponse {
  exported_at: string;
  user_id: string;
  quotes: unknown[];
  clients: unknown[];
  quote_requests: unknown[];
}

export type QuoteRequestStatus = 'new' | 'viewed' | 'quoted';

export interface QuoteRequest {
  id: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  project_description: string;
  address: string | null;
  status: QuoteRequestStatus;
  created_at: string;
  updated_at: string;
}

export interface QuoteRequestListResponse {
  quote_requests: QuoteRequest[];
  next_cursor: string | null;
  has_more: boolean;
  new_count: number;
}

export interface SubmitQuoteRequestInput {
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  project_description: string;
  address?: string;
  _hp?: string;
}

export interface SubmitQuoteRequestResponse {
  id: string;
  created_at: string;
}

export interface ConvertQuoteRequestResponse {
  client_id: string;
  quote_id: string;
}

export interface StatusBreakdown {
  draft: number;
  sent: number;
  accepted: number;
  rejected: number;
  invoiced: number;
  paid: number;
}

export interface AnalyticsResponse {
  period: string;
  total_quotes: number;
  accepted_quotes: number;
  total_revenue: number;
  avg_job_value: number;
  close_rate: number;
  status_breakdown: StatusBreakdown;
}

export class ApiError extends Error {
  status: number;
  errorKey: string;

  constructor(message: string, status: number, errorKey: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorKey = errorKey;
  }
}

// --- Share types ---

export interface ShareQuoteResponse {
  share_token: string;
}

export interface SharedQuoteContractor {
  company_name: string;
  address: string;
  reg_code: string;
  vat_number: string;
}

export interface SharedQuoteItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface SharedQuoteData {
  items: SharedQuoteItem[];
  grand_total: number;
  notes: string | null;
  client_name: string | null;
  client_address: string | null;
  created_at: string;
  vat_rate: number;
  markup_percent: number;
  payment_term_days: number;
  validity_days: number;
}

export interface ClientResponse {
  name: string;
  message: string | null;
  response: 'accepted' | 'rejected';
  responded_at: string;
}

export interface SharedQuoteView {
  contractor: SharedQuoteContractor;
  quote: SharedQuoteData;
  status: string;
  client_response: ClientResponse | null;
  invoice: InvoiceSharedView | null;
}

export interface RespondToQuoteInput {
  name: string;
  message?: string;
  response: 'accepted' | 'rejected';
  _hp: string;
}

export interface PriceCheckItemSuggestion {
  description: string;
  current_price: number;
  suggested_low: number;
  suggested_high: number;
  market_position: 'below' | 'competitive' | 'above';
}

export interface PriceCheckResponse {
  item_suggestions: PriceCheckItemSuggestion[];
  overall_assessment: string;
  currency: string;
  generated_at: string;
}

export type PaymentStatus = 'unpaid' | 'paid';

export interface Invoice {
  id: string;
  quote_id: string;
  invoice_number: string;
  client_name: string | null;
  amount: number;
  currency: string;
  issued_at: string;
  due_date: string;
  payment_status: PaymentStatus;
  paid_at: string | null;
  payment_method: string | null;
  payment_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceListResponse {
  invoices: Invoice[];
  next_cursor: string | null;
}

export interface InvoiceSharedView {
  invoice_number: string;
  amount: number;
  due_date: string;
  payment_status: PaymentStatus;
  paid_at: string | null;
}

export interface UpdatePaymentResponse {
  id: string;
  payment_status: PaymentStatus;
  paid_at: string | null;
  payment_method: string | null;
  payment_notes: string | null;
  updated_at: string;
}

// --- Profile types ---
// ContractorProfile is used for both input (PUT body) and public response (GET).
// They're structurally identical by design — the PUT accepts the same shape
// the public GET returns. Using a single type avoids SonarQube duplication warnings.

export interface ContractorProfile {
  company_name: string;
  tagline: string;
  phone: string;
  email: string;
  logo_url: string;
}

/** Alias for clarity in mutation code */
export type ContractorProfileInput = ContractorProfile;

export interface ContractorProfileFull extends ContractorProfile {
  id: string;
  user_id: string;
  public_id: string | null;
  created_at: string;
  updated_at: string;
}

// --- Job types ---

export interface JobResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  quote_id: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface CreateJobResponse {
  id: string;
  status: 'pending';
}
